import Component from '@glimmer/component'
import { tracked } from '@glimmer/tracking'
import { service } from '@ember/service'
import { action } from '@ember/object'
import type RouterService from '@ember/routing/router-service'
import type Store from '@ember-data/store'
import { Changeset } from 'ember-changeset'
import type { BufferedChangeset } from 'ember-changeset/types'
import { didCancel, task } from 'ember-concurrency'
import type FileDownload from 'teacher-ui-client/services/file-download'
import StudentValidator from 'teacher-ui-client/student/validator'
import lookupValidator from 'teacher-ui-client/utils/lookup-validator'
import type { IntlService } from 'ember-intl'
import type FlashQueueService from 'teacher-ui-client/services/flash-queue'
import { optionTemplate, optionTemplateSubLevel } from 'teacher-ui-client/utils/dropdown-templates'
import type { SubscriptionType } from 'teacher-ui-client/subscription-type/subscription-types'
import type Student from 'teacher-ui-client/models/student'
import type ResourceConflictService from 'teacher-ui-client/services/resource-conflict'
import { isConflictError } from 'teacher-ui-client/resource-conflict/error'
import type SessionService from 'teacher-ui-client/services/session'
import type GradeSetsService from 'teacher-ui-client/services/grade-sets'
import type SubscriptionUtilisationService from 'teacher-ui-client/services/subscription-utilisation'
import type ErrorHandlerService from 'teacher-ui-client/services/error-handler'
import type ErrorHandler from 'teacher-ui-client/utils/error-handler'
import type StudentImportService from 'teacher-ui-client/services/student-import'
import { FormField } from 'teacher-ui-client/components/form-field'
import { on } from '@ember/modifier'
import { FormWrapperSubmitButton } from 'teacher-ui-client/components/form-wrapper/submit-button'
import { or, eq } from 'ember-truth-helpers'
import { t } from 'ember-intl'
import submitButtonState from 'teacher-ui-client/helpers/submit-button-state'
import { HRText } from 'teacher-ui-client/components/base/hr-text'
import { UiButtonLink } from 'teacher-ui-client/components/ui-button/link'
import { pick } from 'teacher-ui-client/helpers/pick'
import { ButtonAppearance } from 'teacher-ui-client/components/ui-button/themes'
import type { SelectOptionWithStudentCount } from 'teacher-ui-client/components/form-components/form-select/option'
import type Owner from '@ember/owner'
import type Teacher from 'teacher-ui-client/models/teacher'
import type SchoolClass from 'teacher-ui-client/models/school-class'
import { isPresent } from '@ember/utils'

type SchoolClassOption = SelectOptionWithStudentCount
type TeacherOption = SelectOptionWithStudentCount & { value: SchoolClassOption[] }

interface Args {
  subscriptionType: SubscriptionType
  schoolClassByTeacherOptions: TeacherOption[]
}

interface Signature {
  Args: Args
  Element: HTMLFormElement
}

export class FormCreateStudent extends Component<Signature> {
  @service intl!: IntlService

  @service store!: Store

  @service flashQueue!: FlashQueueService

  @service gradeSets!: GradeSetsService

  @service session!: SessionService

  @service router!: RouterService

  @service subscriptionUtilisation!: SubscriptionUtilisationService

  @service studentImport!: StudentImportService

  @service fileDownload!: FileDownload

  @service resourceConflict!: ResourceConflictService

  @service('error-handler') errorHandlerService!: ErrorHandlerService

  @tracked errorHandler!: ErrorHandler

  csvText = 'Upload a CSV'

  submitState = 'default'

  groupFilterDropdownOptionTemplate = optionTemplate

  groupFilterDropdownSubOptionTemplate = optionTemplateSubLevel

  changeset!: BufferedChangeset

  constructor(owner: Owner, args: Args) {
    super(owner, args)
    const studentData = { gradePosition: null, firstName: '', lastName: '', schoolClass: null }
    this.changeset = Changeset(studentData, lookupValidator(StudentValidator, { intl: this.intl }), StudentValidator)

    this.errorHandler = this.errorHandlerService.create()
  }

  get isSubco() {
    return this.session.currentTeacher.subcoFor(this.args.subscriptionType)
  }

  get studentImportGuidePdf() {
    return 'https://assets.ctfassets.net/ffboqiwq61pe/3VZgUoED8oerJ52VhEE4Y9/51f23e03a8f3a2c0804a0c5f3fe76b34/Student_Import_Guide-fp-92b413dc.pdf'
  }

  get gradeOptions() {
    return this.gradeSets.optionsBy('name')
  }

  /**
   * Generates a sample student Import CSV array for a teacher or subco
   */
  generateStudentSampleImportData(isSubco: boolean): string[][] {
    const gradeSetFormat = this.gradeSets.grades.map((grade) => grade.name).join(', ')
    const fieldPrefix = 'manage.manageClasses.students.addStudents.csv.downloadSampleFields'

    if (isSubco) {
      const row = [
        this.intl.t(`${fieldPrefix}.firstName`),
        this.intl.t(`${fieldPrefix}.lastName`),
        this.intl.t(`${fieldPrefix}.grade`, { gradeSetFormat }),
        this.intl.t(`${fieldPrefix}.teacherEmail`),
        this.intl.t(`${fieldPrefix}.sisId`),
        this.intl.t(`${fieldPrefix}.className`),
      ]
      return Array.from({ length: 4 }, () => row)
    } else {
      const row = [
        this.intl.t(`${fieldPrefix}.firstName`),
        this.intl.t(`${fieldPrefix}.lastName`),
        this.intl.t(`${fieldPrefix}.grade`, { gradeSetFormat }),
        this.intl.t(`${fieldPrefix}.sisId`),
      ]
      return Array.from({ length: 4 }, () => row)
    }
  }

  @action
  async fetchTeacher() {
    const teacherId = this.session.currentTeacher.id
    const include = ['school.teachers', 'school-classes'].join(',')
    await this.store.findRecord<Teacher>('teacher', teacherId, {
      reload: true,
      // @ts-expect-error - include types not correct
      include,
    })
  }

  createStudent = task({ drop: true }, async (newStudent: Student) => {
    await newStudent.save()
    // update affected records
    void this.subscriptionUtilisation.fetchAll()
    await this.fetchTeacher()
  })

  @action
  async onSubmit(event: Event) {
    event.preventDefault()

    const { changeset, errorHandler } = this

    const schoolClassId = changeset.get('schoolClass')
    const schoolClasses = schoolClassId ? [this.store.peekRecord<SchoolClass>('school-class', schoolClassId)] : []

    const newStudent = this.store.createRecord<Student>('student', {
      firstName: changeset.get('firstName'),
      lastName: changeset.get('lastName'),
      gradePosition: changeset.get('gradePosition'),
      school: this.session.currentSchool,
      schoolClasses: schoolClasses.filter(isPresent),
    })

    const name = newStudent.fullName

    try {
      await this.createStudent.perform(newStudent)

      const message = this.intl.t('manage.manageClasses.students.addStudents.form.successMessage', { name })
      this.flashQueue.addSuccess({ subtitle: message })

      // reset the form data
      this.changeset.rollback()

      this.router.refresh('manage.students')
    } catch (e: any) {
      if (!didCancel(e)) {
        const errors = e?.errors || []
        errorHandler.handle(errors)
        errorHandler.sendOtherErrorsToLog('Error saving a new student')

        // unload the student that wasn't persisted
        newStudent.unloadRecord()

        if (isConflictError(e)) {
          return this.resourceConflict.handleError('update teacher', e)
        }

        if (errorHandler.hasUnhandledErrors()) {
          const message = this.intl.t('manage.manageClasses.students.addStudents.form.failureMessage', { name })
          this.flashQueue.addFail({ subtitle: message })
        }
      }
    }
  }

  @action
  downloadSampleCsv() {
    const data = this.generateStudentSampleImportData(this.isSubco)
    const filename = this.intl.t('manage.manageClasses.students.addStudents.csv.downloadSampleFileName')
    this.fileDownload.downloadAsCsv(data, { filename })
  }

  @action
  async selectCsvFile(fileArray: File[]) {
    if (fileArray && fileArray.length > 0) {
      const file = fileArray[0]

      await this.studentImport.setFile(file)
      void this.router.transitionTo('manage.import.students.validate')
    }
  }

  <template>
    <form data-test-create-students class="flex flex-row flex-wrap gap-2" {{on "submit" this.onSubmit}} ...attributes>
      <FormField
        class="min-w-40 shrink-0 grow basis-0"
        @changeset={{this.changeset}}
        @labelText={{t "manage.firstName"}}
        @propertyName="firstName"
        as |field|
      >
        <field.input placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>

      <FormField
        class="min-w-40 shrink-0 grow basis-0"
        @changeset={{this.changeset}}
        @labelText={{t "manage.lastName"}}
        @propertyName="lastName"
        as |field|
      >
        <field.input placeholder={{field.labelText}} aria-label={{field.labelText}} />
      </FormField>

      <FormField
        @changeset={{this.changeset}}
        @labelText={{t "manage.class"}}
        @propertyName="schoolClass"
        class="min-w-40 shrink-0 grow basis-0"
        as |field|
      >
        <field.select
          @defaultText={{t "manage.manageClasses.students.addStudents.form.selectClass"}}
          @options={{@schoolClassByTeacherOptions}}
          @valueLookupKey="value"
          @secondLevelOnly={{true}}
          @optionTemplate={{this.groupFilterDropdownOptionTemplate}}
          @optionTemplateSubLevel={{this.groupFilterDropdownSubOptionTemplate}}
        />
      </FormField>

      {{! Ensure these elements wrap together }}
      <div class="flex grow flex-row gap-2">
        <FormField
          @changeset={{this.changeset}}
          @labelText="Grade"
          @propertyName="gradePosition"
          class="min-w-40 shrink-0 grow basis-0"
          as |field|
        >
          <field.select
            @options={{this.gradeOptions}}
            @valueLookupKey="value"
            @defaultText={{t "grade.select"}}
            aria-label={{field.labelText}}
          />
        </FormField>

        <FormWrapperSubmitButton
          data-test-add-student-submit
          @disabled={{or this.changeset.isInvalid this.changeset.isPristine}}
          @defaultText={{t "manage.singularActions.submitGo"}}
          @state={{submitButtonState this.createStudent.last this.errorHandler}}
          @appearance={{ButtonAppearance.regular}}
        />
      </div>
    </form>
    <HRText class="my-2" @text={{t "manage.bulkActions.csv.or"}} />
    <h5 class="mt-1 mb-2">
      {{t "manage.manageClasses.students.addStudents.csv.title"}}
    </h5>
    <div class="flex items-center">
      <label data-test-upload-students-csv>
        <input
          class="hidden"
          type="file"
          accept="text/csv, .csv"
          {{on "change" (pick "target.files" this.selectCsvFile)}}
        />
        <UiButtonLink class="font-normal whitespace-nowrap" @appearance={{ButtonAppearance.regular}}>
          {{t "manage.bulkActions.csv.upload"}}
        </UiButtonLink>
      </label>

      <div class="flex flex-col">
        <a
          href={{this.studentImportGuidePdf}}
          target="_blank"
          rel="noopener noreferrer"
          class="ml-4 font-bold
            {{if
              (eq @subscriptionType 'reading')
              'text-oceany-blue-300 hover:text-oceany-blue-300 focus:text-oceany-blue-300'
            }}
            {{if (eq @subscriptionType 'maths') 'text-ms-green-300 hover:text-ms-green-300 focus:text-ms-green-300'}}
            {{if (eq @subscriptionType 'writing') 'text-wl-blue-300 hover:text-wl-blue-300 focus:text-wl-blue-300'}}
            "
        >
          <span>{{t "manage.manageClasses.students.addStudents.csv.downloadGuidePrefix"}} </span>
          <span class="underline">{{t "manage.manageClasses.students.addStudents.csv.downloadGuide"}}</span>
        </a>

        <span
          class="ml-4 cursor-pointer font-bold text-black hover:text-black focus:text-black"
          role="button"
          data-test-download-sample-csv
          {{on "click" this.downloadSampleCsv}}
        >
          <span class="underline">{{t "manage.manageClasses.students.addStudents.csv.downloadSample"}}</span>
          <span>
            {{t "manage.manageClasses.students.addStudents.csv.downloadSampleMotivation"}}
          </span>
        </span>
      </div>

    </div>
  </template>
}

export default FormCreateStudent
